import React, { useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  FilePlus2,
  Paperclip,
  Users,
  Sparkles,
  Plus,
  ArrowUp,
  ArrowDown,
  Trash2,
  X,
  Upload,
  CheckCircle,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

import { useDocumentsData } from "@/components/Documents/documentsData";
import { CharLimitMessage } from "@/components/common/CharLimitMessage";

type NewDocCategory =
  | "SOP"
  | "PGD"
  | "HR"
  | "Auditing"
  | "Compliance"
  | "Clinical"
  | "General";

interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correctIndex: number;
}
interface GeneratedQuiz {
  questions: QuizQuestion[];
}

export interface NewDocumentData {
  title: string;
  type: NewDocCategory;
  mainFile: File;
  supportingFiles: File[];
  assignedEmails: string[];
  reviewInterval: string;
  folder?: string;
  quiz?: GeneratedQuiz;
  roles: string[];
  skipRefresh?: boolean;
}

export interface UpdateDocumentData {
  title: string;
  type: NewDocCategory;
  mainFile?: File | null;
  supportingFiles?: File[];
  assignedEmails: string[];
  reviewInterval: string;
  folder?: string;
  quiz?: GeneratedQuiz;
  roles: string[];
}

const MAX_BULK_FILES = 25;

const REVIEW_INTERVAL_OPTIONS = [
  "Never",
  "Every week",
  "Every 2 weeks",
  "Every month",
  "Every 3 months",
  "Every 6 months",
  "Every year",
  "Every 2 years",
  "Custom",
];

interface UploadFileEntry {
  id: string;
  file: File;
  status: "pending" | "success";
}

interface NewDocumentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (data: NewDocumentData) => void;
  initial?: {
    id: string;
    title: string;
    type: NewDocCategory;
    assignedEmails: string[];
    reviewInterval?: string;
    folder?: string;
    quiz?: GeneratedQuiz;
    mainFile?: { fileName: string; sizeKB: number } | null;
    supportingFiles?: { fileName: string; sizeKB: number }[];
    roles: string[];
  } | null;
  onUpdate?: (id: string, data: UpdateDocumentData) => void;
  folders?: string[];
  onAddFolder?: (name: string) => void;
}

export function NewDocumentModal({
  open,
  onOpenChange,
  onCreate,
  initial,
  onUpdate,
  folders = [],
  onAddFolder,
}: NewDocumentModalProps) {
  const {
    initialMembers = [],
    isMemberLoading,
    darkMode,
    availableRoles,
    isAddDocumentLoading,
    companyFolders,
    isCompanyFoldersLoading,
    handleAddFolder,
    handleGetFolders,
    companyFoldersLoader,
    setFolder,
    folder,
    documentSizeLimitInMbs = 5, // Default to 5MB if not provided
  } = useDocumentsData() || {};

  const [step, setStep] = useState<1 | 2>(1);
  const [title, setTitle] = useState("");
  const [mainFile, setMainFile] = useState<File | null>(null);
  const [supportingFiles, setSupportingFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string>("");
  const [mainFileError, setMainFileError] = useState<string>("");
  const [supportingFileError, setSupportingFileError] = useState<string>("");
  const [folderError, setFolderError] = useState<string>("");
  const [assigned, setAssigned] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(
    new Set(),
  );
  const [expandedRoleGroups, setExpandedRoleGroups] = useState<
    Record<string, boolean>
  >({});
  const [docType, setDocType] = useState<NewDocCategory>("General");
  const [reviewInterval, setReviewInterval] = useState<string>("Never");
  const [customReviewDate, setCustomReviewDate] = useState<string>("");
  const [generating, setGenerating] = useState(false);
  const [folderOptions, setFolderOptions] = useState<string[]>([]);
  const [folderOpen, setFolderOpen] = useState(false);
  const [folderSearch, setFolderSearch] = useState("");
  const folderExists = (name: string) =>
    companyFolders?.some(
      (f: any) => f.folder.toLowerCase() === name.toLowerCase(),
    );
  const [submitted, setSubmitted] = useState(false);
  const sawLoadingRef = useRef(false);
  const [quiz, setQuiz] = useState<GeneratedQuiz | undefined>(undefined);
  const mainFileInputRef = useRef<HTMLInputElement | null>(null);
  const supportFilesInputRef = useRef<HTMLInputElement | null>(null);

  // Multi-document upload state (create mode only)
  const [uploadFiles, setUploadFiles] = useState<UploadFileEntry[]>([]);
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const bulkFileInputRef = useRef<HTMLInputElement | null>(null);

  // Total size of all supporting files (in bytes)
  const totalSupportingSize = useMemo(
    () => supportingFiles.reduce((acc, f) => acc + (f?.size || 0), 0),
    [supportingFiles],
  );

  const totalSupportingExceeded =
    totalSupportingSize > documentSizeLimitInMbs * 1024 * 1024;

  // Helper function to validate file size
  const validateFileSize = (file: File): boolean => {
    const maxSizeInBytes = documentSizeLimitInMbs * 1024 * 1024;
    return file.size <= maxSizeInBytes;
  };

  // Helper function to format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const isEdit = !!initial && !!onUpdate;
  const canProceed = useMemo(
    () => (isEdit ? title.trim().length > 0 : uploadFiles.length > 0),
    [isEdit, title, uploadFiles],
  );

  // Check if all roles are selected
  const allRolesSelected =
    availableRoles?.length > 0 &&
    selectedRoles.length === availableRoles.length;

  // Toggle select all roles
  const toggleSelectAllRoles = () => {
    if (allRolesSelected) {
      // If all are selected, deselect all
      setSelectedRoles([]);
    } else {
      // If not all are selected, select all
      setSelectedRoles(availableRoles.map((role) => role.role));
    }
  };

  const reset = () => {
    setTitle("");
    setMainFile(null);
    setSupportingFiles([]);
    setAssigned([]);
    setSelectedRoles([]);
    setSelectedEmails(new Set());
    setDocType("General");
    setReviewInterval("Never");
    setCustomReviewDate("");
    setFolder("");
    setGenerating(false);
    setQuiz(undefined);
    setStep(1);
    setFileError("");
    setMainFileError("");
    setSupportingFileError("");
    setFolderError("");
    if (mainFileInputRef.current) mainFileInputRef.current.value = "";
    if (supportFilesInputRef.current) supportFilesInputRef.current.value = "";
    setFolderOptions(folders || []);
    setUploadFiles([]);
    setIsBulkUploading(false);
    setIsDragActive(false);
    if (bulkFileInputRef.current) bulkFileInputRef.current.value = "";
  };

  const handleBulkFileSelect = (files: FileList | null) => {
    if (!files || !files.length) return;
    const incoming = Array.from(files);
    const oversized = incoming.filter((f) => !validateFileSize(f));
    const valid = incoming.filter((f) => validateFileSize(f));

    if (oversized.length > 0) {
      setFileError(
        `${oversized.length} file(s) exceed ${documentSizeLimitInMbs}MB and were excluded.`,
      );
    } else {
      setFileError("");
    }

    if (!valid.length) return;

    setUploadFiles((prev) => {
      const existingNames = new Set(prev.map((u) => u.file.name));
      const additions = valid
        .filter((f) => !existingNames.has(f.name))
        .map((file) => ({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          file,
          status: "pending" as const,
        }));
      const combined = [...prev, ...additions];
      if (combined.length > MAX_BULK_FILES) {
        setFileError(
          `Maximum ${MAX_BULK_FILES} documents per upload. Oldest files were removed.`,
        );
        return combined.slice(-MAX_BULK_FILES);
      }
      return combined;
    });
  };

  const removeUploadFile = (id: string) => {
    setUploadFiles((prev) => prev.filter((u) => u.id !== id));
  };

  // Remove supporting files that are individually oversize or remove largest files
  // until total supporting size is within the configured limit.
  const removeOversizeSupportingFiles = () => {
    const limitBytes = documentSizeLimitInMbs * 1024 * 1024;

    const anyIndividualOversize = supportingFiles.some(
      (f) => f.size > limitBytes,
    );
    const totalExceeds = totalSupportingSize > limitBytes;

    // If any individual file is oversize OR the total exceeds the limit,
    // clear the entire supporting files selection as requested.
    if (anyIndividualOversize || totalExceeds) {
      setSupportingFiles([]);
      setSupportingFileError("");
      if (supportFilesInputRef.current) supportFilesInputRef.current.value = "";
    }
  };

  const handleCreate = () => {
    if (!uploadFiles.length) return;

    if (!folder) {
      setFolderError("Please select a folder before uploading documents.");
      return;
    }
    setFolderError("");

    const oversized = uploadFiles.filter(
      (entry) => !validateFileSize(entry.file),
    );
    if (oversized.length) {
      setFileError(
        `${oversized.length} file(s) exceed ${documentSizeLimitInMbs}MB and must be removed before uploading.`,
      );
      return;
    }

    const emails = Array.from(
      new Set(
        initialMembers
          .filter((m) => selectedRoles.includes(m.role))
          .map((m) => m.email)
          .concat(Array.from(selectedEmails)),
      ),
    );

    setIsBulkUploading(true);

    uploadFiles.forEach((entry, index) => {
      setTimeout(() => {
        onCreate({
          // CompanyDocumentModel.Title is [MaxLength(500)] in the API
          title: entry.file.name.replace(/\.[^.]+$/, "").slice(0, 500),
          type: docType,
          mainFile: entry.file,
          supportingFiles: [],
          assignedEmails: emails,
          reviewInterval,
          customReviewDate: reviewInterval === "Custom" ? customReviewDate : undefined,
          folder: folder,
          quiz: undefined,
          roles: selectedRoles,
          skipRefresh: index !== uploadFiles.length - 1,
        });
        setUploadFiles((prev) =>
          prev.map((u) =>
            u.id === entry.id ? { ...u, status: "success" } : u,
          ),
        );
      }, index * 300);
    });

    setTimeout(
      () => {
        setIsBulkUploading(false);
        onOpenChange(false);
        reset();
      },
      uploadFiles.length * 300 + 500,
    );
    // keep open while uploads are staggered; closes automatically once done
  };

  const handleUpdate = () => {
    if (!isEdit || !initial) return;

    // Final validation before updating
    if (mainFile && !validateFileSize(mainFile)) {
      setMainFileError(
        `Main document must be ${documentSizeLimitInMbs}MB or less.`,
      );
      return;
    }

    const invalidSupportingFile = supportingFiles.find(
      (file) => !validateFileSize(file),
    );
    if (invalidSupportingFile) {
      setSupportingFileError(
        `Each supporting document must be ${documentSizeLimitInMbs}MB or less.`,
      );
      return;
    }

    // Total supporting size should not exceed configured limit
    if (totalSupportingExceeded) {
      setSupportingFileError(
        `Total supporting documents size cannot exceed ${documentSizeLimitInMbs}MB.`,
      );
      return;
    }

    const emails = Array.from(
      new Set(
        initialMembers
          .filter((m) => selectedRoles.includes(m.role))
          .map((m) => m.email)
          .concat(Array.from(selectedEmails)),
      ),
    );
    setSubmitted(true);
    onUpdate?.(initial.id, {
      title: title.trim(),
      type: docType,
      mainFile: mainFile || null,
      supportingFiles: supportingFiles.length ? supportingFiles : undefined,
      assignedEmails: emails,
      reviewInterval,
      customReviewDate: reviewInterval === "Custom" ? customReviewDate : undefined,
      folder: folder,
      roles: selectedRoles,
      quiz,
    });
    // keep open; will close after loading finishes
  };

  // const handleSkipCreate = () => {
  //   if (!canProceed || !mainFile) return;
  //   onCreate({
  //     title: title.trim(),
  //     type: docType,
  //     mainFile,
  //     supportingFiles,
  //     assignedEmails: [],
  //     quiz: undefined,
  //   });
  //   reset();
  //   // onOpenChange(false);
  // };

  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve) => {
      const isTextLike =
        file.type.startsWith("text/") ||
        [
          "application/json",
          "application/xml",
          "application/xhtml+xml",
        ].includes(file.type) ||
        /\.(txt|md|csv|log|json|xml)$/i.test(file.name);
      if (!isTextLike) return resolve("");
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => resolve("");
      reader.readAsText(file);
    });
  };

  const addQuestion = () => {
    setQuiz((prev) => {
      const nextIdNum = (prev?.questions.length || 0) + 1;
      const newQ: QuizQuestion = {
        id: `q${nextIdNum}`,
        question: "",
        options: ["", "", "", ""],
        correctIndex: 0,
      };
      return { questions: [...(prev?.questions || []), newQ] };
    });
  };

  const deleteQuestion = (index: number) => {
    setQuiz((prev) => {
      if (!prev) return prev;
      const qs = [...prev.questions];
      qs.splice(index, 1);
      return { questions: qs };
    });
  };

  const moveQuestion = (index: number, delta: number) => {
    setQuiz((prev) => {
      if (!prev) return prev;
      const qs = [...prev.questions];
      const newIndex = index + delta;
      if (newIndex < 0 || newIndex >= qs.length) return prev;
      const [moved] = qs.splice(index, 1);
      qs.splice(newIndex, 0, moved);
      return { questions: qs };
    });
  };

  const updateQuestionText = (index: number, text: string) => {
    setQuiz((prev) => {
      if (!prev) return prev;
      const qs = [...prev.questions];
      qs[index] = { ...qs[index], question: text };
      return { questions: qs };
    });
  };

  const updateOptionText = (qIndex: number, optIndex: number, text: string) => {
    setQuiz((prev) => {
      if (!prev) return prev;
      const qs = [...prev.questions];
      const q = qs[qIndex];
      const opts = [...q.options];
      opts[optIndex] = text;
      qs[qIndex] = { ...q, options: opts };
      return { questions: qs };
    });
  };

  const setCorrectOption = (qIndex: number, optIndex: number) => {
    setQuiz((prev) => {
      if (!prev) return prev;
      const qs = [...prev.questions];
      const q = qs[qIndex];
      qs[qIndex] = { ...q, correctIndex: optIndex };
      return { questions: qs };
    });
  };

  const generateQuiz = async () => {
    if (!mainFile) return;
    setGenerating(true);
    try {
      const texts: string[] = [];
      const mainText = await readFileAsText(mainFile);
      if (mainText) texts.push(mainText);
      for (const f of supportingFiles) {
        const t = await readFileAsText(f);
        if (t) texts.push(t);
      }
      const combined = texts.join("\n\n").slice(0, 50000);
      const stop = new Set([
        "the",
        "and",
        "for",
        "with",
        "that",
        "this",
        "from",
        "have",
        "has",
        "are",
        "was",
        "were",
        "will",
        "shall",
        "can",
        "may",
        "not",
        "but",
        "into",
        "about",
        "after",
        "before",
        "during",
        "to",
        "of",
        "in",
        "on",
        "by",
        "at",
        "as",
        "or",
        "it",
        "be",
        "is",
        "a",
        "an",
      ]);
      const words = combined
        .replace(/[^A-Za-z0-9\s]/g, " ")
        .split(/\s+/)
        .map((w) => w.trim())
        .filter((w) => w.length > 3 && !stop.has(w.toLowerCase()));
      const uniqueWords = Array.from(new Set(words));
      const sentences = combined
        .replace(/\s+/g, " ")
        .split(/(?<=[\.!?])\s+/)
        .filter((s) => s.trim().length > 20)
        .slice(0, 40);
      const selected = sentences.slice(0, Math.min(5, sentences.length));
      const questions: QuizQuestion[] = selected.map((s, idx) => {
        const sWords = s.split(/\s+/);
        let blankIndex = sWords.findIndex(
          (w) => w.replace(/[^A-Za-z]/g, "").length > 5,
        );
        if (blankIndex === -1) blankIndex = Math.min(3, sWords.length - 1);
        const answer = sWords[blankIndex]?.replace(/[^A-Za-z0-9-]/g, "");
        const distractors = uniqueWords
          .filter((w) => w.toLowerCase() !== String(answer).toLowerCase())
          .sort(() => Math.random() - 0.5)
          .slice(0, 3);
        const opts = [answer, ...distractors]
          .filter(Boolean)
          .slice(0, 4)
          .sort(() => Math.random() - 0.5) as string[];
        const correctIndex = Math.max(
          0,
          opts.findIndex((o) => o === answer),
        );
        const questionText = sWords
          .map((w, i) => (i === blankIndex ? "_____" : w))
          .join(" ");
        return {
          id: `q${idx + 1}`,
          question: questionText,
          options: opts.length ? opts : [String(answer || "")],
          correctIndex: correctIndex === -1 ? 0 : correctIndex,
        };
      });
      if (!questions.length) {
        const base = (mainFile?.name || "document").replace(/\.[^.]+$/, "");
        const opts = ["True", "False", "Not sure", ""];
        setQuiz({
          questions: [
            {
              id: "q1",
              question: `${base}: I have read and understand this document.`,
              options: opts,
              correctIndex: 0,
            },
            {
              id: "q2",
              question: `This ${docType} requires annual review.`,
              options: opts,
              correctIndex: 0,
            },
            {
              id: "q3",
              question: `I know where to locate the latest ${docType} version.`,
              options: opts,
              correctIndex: 0,
            },
          ],
        });
      } else {
        setQuiz({ questions });
      }
    } finally {
      setGenerating(false);
    }
  };

  const toggleAssigned = (email: string) => {
    setAssigned((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email],
    );
  };

  React.useEffect(() => {
    if (!open) return;

    if (isEdit && initial) {
      setReviewInterval(
        initial.reviewInterval ||
          (initial.type === "SOP" ? "Every 2 years" : "Never"),
      );
    } else {
      setReviewInterval(docType === "SOP" ? "Every 2 years" : "Never");
    }
  }, [open, isEdit, initial, docType]);

  React.useEffect(() => {
    setFolderOptions(Array.from(new Set([...(folders || [])])).sort());
    if (isEdit && initial && open) {
      setTitle(initial.title);
      setDocType(initial.type);
      const roles = availableRoles
        ?.filter((r: any) => initial.roles.includes(r.id))
        .map((r: any) => r.role);
      setSelectedRoles(roles);
      setReviewInterval(
        initial.reviewInterval ||
          (initial.type === "SOP" ? "Every 2 years" : "Never"),
      );
      setFolder(initial.folder || "");

      // ✅ Preload file names (just for display, not real File objects)
      if (initial.mainFile) {
        setMainFile({
          name: initial.mainFile.fileName,
          size: initial.mainFile.sizeKB * 1024,
        } as any);
      }
      if (initial.supportingFiles?.length) {
        const fakeFiles = initial.supportingFiles.map((f) => ({
          name: f.fileName,
          size: f.sizeKB * 1024,
        })) as any[];
        setSupportingFiles(fakeFiles);
      }
    }
  }, [isEdit, initial, open, folders]);

  React.useEffect(() => {
    if (submitted && isAddDocumentLoading) {
      sawLoadingRef.current = true;
    }
  }, [submitted, isAddDocumentLoading]);

  React.useEffect(() => {
    if (submitted && sawLoadingRef.current && !isAddDocumentLoading) {
      onOpenChange(false);
      setSubmitted(false);
      sawLoadingRef.current = false;
    }
  }, [submitted, isAddDocumentLoading]);

  React.useEffect(() => {
    if (open) {
      handleGetFolders?.(); // ✅ fetch folders when popup opens
    }
  }, [open]);

  const handleCreateAndSelectFolder = async (folderName: string) => {
    try {
      // Create the folder
      await handleAddFolder?.(folderName);
      setFolderOpen(false);
      setFolderSearch("");

      await handleGetFolders?.();
      // Refresh folders list in background
    } catch (error) {
      console.error("Failed to create folder:", error);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        className="max-w-3xl z-[9999] pointer-events-auto overflow-auto max-h-[95vh] custom-scrollbar"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FilePlus2 className="w-5 h-5 text-blue-600" />
            {isEdit ? "Edit Document" : "Upload Documents"}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            {isEdit
              ? `Maximum file size: ${documentSizeLimitInMbs}MB per file`
              : `Upload up to ${MAX_BULK_FILES} documents at once. All documents will use the same type, folder, and review settings.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {step === 1 && (
            <>
              <div className="space-y-4">
                {isEdit && (
                  <div>
                    <Label htmlFor="doc-title">Document Name *</Label>
                    <Input
                      id="doc-title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Enter document name"
                      className="mt-1"
                      disabled={isAddDocumentLoading}
                    />
                  </div>
                )}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {isEdit && (
                    <div>
                      <Label>Type *</Label>
                      <Select
                        value={docType}
                        onValueChange={(v) => setDocType(v as NewDocCategory)}
                        disabled={isAddDocumentLoading}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent className="z-[10000]">
                          <SelectItem value="SOP">SOP</SelectItem>
                          <SelectItem value="PGD">PGD</SelectItem>
                          <SelectItem value="HR">HR</SelectItem>
                          <SelectItem value="Auditing">Auditing</SelectItem>
                          <SelectItem value="Compliance">Compliance</SelectItem>
                          <SelectItem value="Clinical">Clinical</SelectItem>
                          <SelectItem value="General">General</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {isEdit && (
                    <div className="relative">
                      <Label htmlFor="doc-folder">Folder</Label>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={folderOpen}
                        onClick={() => setFolderOpen((prev) => !prev)}
                        disabled={isAddDocumentLoading || companyFoldersLoader}
                        className="justify-between w-full h-auto px-3 py-3 mt-1 text-sm text-left whitespace-normal bg-white border border-gray-300 cursor-pointer hover:bg-gray-50"
                      >
                        <span className="flex-1 text-left break-words">
                          {companyFoldersLoader ? (
                            <span className="flex items-center gap-2">
                              <div className="w-3 h-3 border-2 border-gray-400 rounded-full border-t-transparent animate-spin" />
                              Loading...
                            </span>
                          ) : (
                            companyFolders?.find((f: any) => f._id === folder)
                              ?.folder ||
                            folder ||
                            "Select folder"
                          )}
                        </span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className={`h-4 w-4 transition-transform duration-200 flex-shrink-0 ml-2 ${
                            folderOpen ? "rotate-180" : ""
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </Button>

                      {/* Dropdown Panel */}
                      {folderOpen && (
                        <div
                          className="absolute z-[9999] mt-1 w-full min-w-[260px] bg-white border border-gray-200 rounded-md shadow-lg p-2"
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          {/* Search Input */}
                          <input
                            type="text"
                            placeholder="Search folders..."
                            value={folderSearch}
                            onChange={(e) => setFolderSearch(e.target.value)}
                            maxLength={500}
                            autoFocus
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400"
                          />
                          <CharLimitMessage value={folderSearch} max={500} className="text-xs" />

                          <div className="mt-2 overflow-y-auto text-sm max-h-48">
                            {/* Create Folder Option */}
                            {folderSearch.trim().length > 0 &&
                              !folderExists(folderSearch.trim()) && (
                                <div
                                  onClick={() =>
                                    handleCreateAndSelectFolder(
                                      folderSearch.trim(),
                                    )
                                  }
                                  className="px-3 py-2 text-blue-600 underline rounded-md cursor-pointer hover:bg-blue-50"
                                >
                                  Create "{folderSearch}"
                                </div>
                              )}

                            {/* Folder List */}
                            {!isCompanyFoldersLoading &&
                            companyFolders?.length > 0
                              ? companyFolders
                                  .filter((folderItem: any) =>
                                    folderItem.folder
                                      .toLowerCase()
                                      .includes(folderSearch.toLowerCase()),
                                  )
                                  .map((folderItem: any) => (
                                    <div
                                      key={folderItem._id}
                                      onClick={() => {
                                        setFolder(folderItem._id);
                                        setFolderOpen(false);
                                        setFolderSearch("");
                                      }}
                                      className="px-3 py-2 rounded-md cursor-pointer hover:bg-gray-100"
                                    >
                                      {folderItem.folder}
                                    </div>
                                  ))
                              : !isCompanyFoldersLoading && (
                                  <div className="px-3 py-2 text-gray-500">
                                    No folders found
                                  </div>
                                )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {!isEdit && (
                <div>
                  <Label className="text-base font-semibold">
                    1. Select Folder *
                  </Label>
                  <div className="relative mt-2">
                    <Button
                      variant={folderError ? "destructive" : "outline"}
                      role="combobox"
                      aria-expanded={folderOpen}
                      onClick={() => setFolderOpen((prev) => !prev)}
                      disabled={isAddDocumentLoading || companyFoldersLoader}
                      className="justify-between w-full h-auto px-3 py-3 text-sm text-left whitespace-normal bg-white border border-gray-300 cursor-pointer hover:bg-gray-50"
                    >
                      <span className="flex-1 text-left break-words">
                        {companyFoldersLoader ? (
                          <span className="flex items-center gap-2">
                            <div className="w-3 h-3 border-2 border-gray-400 rounded-full border-t-transparent animate-spin" />
                            Loading...
                          </span>
                        ) : (
                          companyFolders?.find((f: any) => f._id === folder)
                            ?.folder ||
                          folder ||
                          "Select folder"
                        )}
                      </span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-4 w-4 transition-transform duration-200 flex-shrink-0 ml-2 ${
                          folderOpen ? "rotate-180" : ""
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </Button>

                    {/* Dropdown Panel */}
                    {folderOpen && (
                      <div
                        className="absolute z-[9999] mt-1 w-full min-w-[260px] bg-white border border-gray-200 rounded-md shadow-lg p-2"
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        {/* Search Input */}
                        <input
                          type="text"
                          placeholder="Search folders..."
                          value={folderSearch}
                          onChange={(e) => setFolderSearch(e.target.value)}
                          maxLength={500}
                          autoFocus
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400"
                        />
                        <CharLimitMessage value={folderSearch} max={500} className="text-xs" />

                        <div className="mt-2 overflow-y-auto text-sm max-h-48">
                          {/* Create Folder Option */}
                          {folderSearch.trim().length > 0 &&
                            !folderExists(folderSearch.trim()) && (
                              <div
                                onClick={() =>
                                  handleCreateAndSelectFolder(
                                    folderSearch.trim(),
                                  )
                                }
                                className="px-3 py-2 text-blue-600 underline rounded-md cursor-pointer hover:bg-blue-50"
                              >
                                Create "{folderSearch}"
                              </div>
                            )}

                          {/* Folder List */}
                          {!isCompanyFoldersLoading &&
                          companyFolders?.length > 0
                            ? companyFolders
                                .filter((folderItem: any) =>
                                  folderItem.folder
                                    .toLowerCase()
                                    .includes(folderSearch.toLowerCase()),
                                )
                                .map((folderItem: any) => (
                                  <div
                                    key={folderItem._id}
                                    onClick={() => {
                                      setFolder(folderItem._id);
                                      setFolderOpen(false);
                                      setFolderSearch("");
                                      setFolderError("");
                                    }}
                                    className="px-3 py-2 rounded-md cursor-pointer hover:bg-gray-100"
                                  >
                                    {folderItem.folder}
                                  </div>
                                ))
                            : !isCompanyFoldersLoading && (
                                <div className="px-3 py-2 text-gray-500">
                                  No folders found
                                </div>
                              )}
                        </div>
                      </div>
                    )}
                  </div>
                  {folderError && (
                    <p className="mt-1 text-xs text-red-500">{folderError}</p>
                  )}
                </div>
              )}

              {isEdit ? (
                <>
                  <div className="space-y-2">
                    <Label>Main Document *</Label>
                    <div className="flex items-center gap-3">
                      <Input
                        ref={mainFileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx,.txt,.rtf,image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0] ?? null;
                          if (file) {
                            if (!validateFileSize(file)) {
                              setMainFileError(
                                `Main document must be ${documentSizeLimitInMbs}MB or less.`,
                              );
                              setMainFile(null);
                              if (mainFileInputRef.current)
                                mainFileInputRef.current.value = "";
                            } else {
                              setMainFileError("");
                              setMainFile(file);
                            }
                          } else {
                            setMainFile(null);
                            setMainFileError("");
                          }
                        }}
                        disabled={isAddDocumentLoading}
                      />
                      {mainFile && (
                        <div className="text-xs text-gray-600">
                          Selected:{" "}
                          <span className="font-medium text-gray-800">
                            {mainFile.name}
                          </span>
                          <span className="ml-2 text-gray-500">
                            ({formatFileSize(mainFile.size)})
                          </span>
                        </div>
                      )}
                    </div>
                    {mainFileError && (
                      <div className="mt-1 text-xs text-red-500">
                        {mainFileError}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Supporting Documents</Label>
                    <Input
                      ref={supportFilesInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.rtf,image/*"
                      onChange={(e) => {
                        const files = Array.from(e.target.files ?? []);
                        const invalidFiles = files.filter(
                          (file) => !validateFileSize(file),
                        );

                        if (invalidFiles.length > 0) {
                          setSupportingFileError(
                            `${invalidFiles.length} file(s) exceed ${documentSizeLimitInMbs}MB limit.`,
                          );
                        } else {
                          setSupportingFileError("");
                        }

                        setSupportingFiles(files);
                      }}
                      disabled={isAddDocumentLoading}
                    />

                    {supportingFileError && (
                      <div className="mt-1 text-xs text-red-500">
                        {supportingFileError}
                      </div>
                    )}
                    {totalSupportingExceeded && (
                      <div className="mt-1 text-xs text-red-500">
                        {`Total supporting documents size (${formatFileSize(totalSupportingSize)}) exceeds ${documentSizeLimitInMbs}MB.`}
                      </div>
                    )}
                    {supportingFiles.length > 0 && (
                      <div className="mt-2 overflow-auto border border-gray-200 divide-y rounded-md max-h-30">
                        {supportingFiles.map((f, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2 px-3 py-2 text-sm"
                          >
                            <Paperclip className="w-4 h-4 text-gray-500" />
                            <div className="flex-1 truncate">{f.name}</div>
                            <div className="text-xs text-gray-500">
                              {formatFileSize(f.size)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Button to clear entire supporting selection when limits exceeded */}
                    {supportingFiles.length > 0 &&
                      (supportingFiles.some(
                        (f) => f.size > documentSizeLimitInMbs * 1024 * 1024,
                      ) ||
                        totalSupportingExceeded) && (
                        <div className="flex justify-end mt-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={removeOversizeSupportingFiles}
                            disabled={isAddDocumentLoading}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <X className="w-4 h-4 mr-2" />
                            Clear Supporting Files
                          </Button>
                        </div>
                      )}
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <Label className="text-base font-semibold">
                    2. Add your documents
                  </Label>
                  <p className="text-xs text-gray-600 mt-1 mb-3">Documents *</p>
                  <div
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsDragActive(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsDragActive(false);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsDragActive(false);
                      handleBulkFileSelect(e.dataTransfer.files);
                    }}
                    onClick={() => bulkFileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                      isDragActive
                        ? "border-blue-400 bg-blue-50"
                        : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"
                    }`}
                  >
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900">
                      Drag & drop documents here
                    </p>
                    <p className="text-xs text-gray-600 mt-1">or</p>
                    <Button
                      type="button"
                      variant="ghost"
                      className="text-blue-600 hover:text-blue-700 font-semibold mt-1 h-auto p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        bulkFileInputRef.current?.click();
                      }}
                      disabled={isAddDocumentLoading || isBulkUploading}
                    >
                      Choose files
                    </Button>
                    <p className="text-xs text-gray-600 mt-2">
                      Up to {MAX_BULK_FILES} documents,{" "}
                      {documentSizeLimitInMbs}MB max per file.
                    </p>
                    <input
                      ref={bulkFileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.rtf,image/*"
                      onChange={(e) => {
                        handleBulkFileSelect(e.target.files);
                        if (bulkFileInputRef.current)
                          bulkFileInputRef.current.value = "";
                      }}
                      className="hidden"
                      disabled={isAddDocumentLoading || isBulkUploading}
                    />
                  </div>

                  {fileError && (
                    <div className="mt-1 text-xs text-red-500">
                      {fileError}
                    </div>
                  )}

                  {uploadFiles.length > 0 && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-gray-900">
                          {uploadFiles.length} document
                          {uploadFiles.length === 1 ? "" : "s"} selected
                        </p>
                        <button
                          type="button"
                          onClick={() => bulkFileInputRef.current?.click()}
                          className="text-sm text-blue-600 hover:text-blue-700 font-semibold"
                          disabled={isBulkUploading}
                        >
                          + Add more documents
                        </button>
                      </div>
                      <div className="overflow-auto border border-gray-200 divide-y rounded-md max-h-48">
                        {uploadFiles.map((entry) => (
                          <div
                            key={entry.id}
                            className="flex items-center gap-2 px-3 py-2 text-sm"
                          >
                            <Paperclip className="w-4 h-4 text-gray-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0 truncate">
                              {entry.file.name}
                            </div>
                            <div className="text-xs text-gray-500 flex-shrink-0">
                              {formatFileSize(entry.file.size)}
                            </div>
                            {entry.status === "success" ? (
                              <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                            ) : (
                              <button
                                type="button"
                                onClick={() => removeUploadFile(entry.id)}
                                className="p-1 rounded hover:bg-gray-100 flex-shrink-0"
                                disabled={isBulkUploading}
                              >
                                <X className="w-4 h-4 text-gray-500" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {isEdit ? (
                <div>
                  <Label>Set Review Interval</Label>
                  <Select
                    value={reviewInterval}
                    onValueChange={setReviewInterval}
                    disabled={isAddDocumentLoading}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select interval" />
                    </SelectTrigger>
                    <SelectContent className="z-[10000]">
                      {REVIEW_INTERVAL_OPTIONS.map((interval) => (
                        <SelectItem key={interval} value={interval}>
                          {interval}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {reviewInterval === "Custom" && (
                    <div className="mt-3">
                      <Label className="text-sm">Select review date</Label>
                      <Input
                        type="date"
                        value={customReviewDate}
                        onChange={(e) => setCustomReviewDate(e.target.value)}
                        className="mt-1"
                        disabled={isAddDocumentLoading}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <Label className="text-base font-semibold">
                    3. Document settings (applied to all documents)
                  </Label>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mt-3">
                    <div>
                      <Label className="text-sm">Document type</Label>
                      <Select
                        value={docType}
                        onValueChange={(v) => setDocType(v as NewDocCategory)}
                        disabled={isAddDocumentLoading}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent className="z-[10000]">
                          <SelectItem value="SOP">SOP</SelectItem>
                          <SelectItem value="PGD">PGD</SelectItem>
                          <SelectItem value="HR">HR</SelectItem>
                          <SelectItem value="Auditing">Auditing</SelectItem>
                          <SelectItem value="Compliance">Compliance</SelectItem>
                          <SelectItem value="Clinical">Clinical</SelectItem>
                          <SelectItem value="General">General</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">Review interval</Label>
                      <Select
                        value={reviewInterval}
                        onValueChange={setReviewInterval}
                        disabled={isAddDocumentLoading}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select interval" />
                        </SelectTrigger>
                        <SelectContent className="z-[10000]">
                          {REVIEW_INTERVAL_OPTIONS.map((interval) => (
                            <SelectItem key={interval} value={interval}>
                              {interval}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {reviewInterval === "Custom" && (
                        <div className="mt-3">
                          <Label className="text-sm">Select review date</Label>
                          <Input
                            type="date"
                            value={customReviewDate}
                            onChange={(e) => setCustomReviewDate(e.target.value)}
                            className="mt-1"
                            disabled={isAddDocumentLoading}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div>
                  {isEdit ? (
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-blue-600" />
                      <Label>Assign to roles</Label>
                    </div>
                  ) : (
                    <>
                      <Label className="text-base font-semibold">
                        4. Access & compliance
                      </Label>
                      <p className="text-sm text-gray-600">
                        Assign access by role
                      </p>
                    </>
                  )}
                </div>
                <div className="space-y-2">
                  {availableRoles?.map((r) => {
                    const members = initialMembers.filter(
                      (m) => m.role === r.role,
                    );
                    const count = members.length;
                    const checked = selectedRoles.includes(r.role);
                    const isExpanded = expandedRoleGroups[r.role] || false;

                    return (
                      <div
                        key={r.id}
                        className="border rounded-md"
                      >
                        <label className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() =>
                              setSelectedRoles((prev) =>
                                checked
                                  ? prev.filter((role) => role !== r.role)
                                  : [...prev, r.role],
                              )
                            }
                            disabled={isAddDocumentLoading}
                          />
                          {members.length > 0 ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                setExpandedRoleGroups((prev) => ({
                                  ...prev,
                                  [r.role]: !prev[r.role],
                                }));
                              }}
                              className="flex items-center justify-center flex-shrink-0 w-6 h-6 rounded hover:bg-gray-100"
                            >
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronRight className="w-4 h-4" />
                              )}
                            </button>
                          ) : (
                            <div className="flex-shrink-0 w-6 h-6" />
                          )}
                          <div className="flex-1 text-sm">
                            <div className="font-medium text-gray-900">
                              {r.role}
                            </div>
                            <div className="text-xs text-gray-600">
                              {count} member{count === 1 ? "" : "s"}
                            </div>
                          </div>
                        </label>

                        {isExpanded && members.length > 0 && (
                          <div className="p-3 space-y-2 border-t bg-gray-50">
                            {members.map((member) => {
                              const memberChecked =
                                checked || selectedEmails.has(member.email);
                              return (
                                <label
                                  key={member.email}
                                  className="flex items-center gap-2 p-2 text-sm rounded cursor-pointer hover:bg-gray-100"
                                >
                                  <Checkbox
                                    checked={memberChecked}
                                    onCheckedChange={(nextChecked) => {
                                      setSelectedEmails((prev) => {
                                        const next = new Set(prev);
                                        if (nextChecked) {
                                          next.add(member.email);
                                        } else {
                                          next.delete(member.email);
                                        }
                                        return next;
                                      });
                                    }}
                                    disabled={isAddDocumentLoading || checked}
                                  />
                                  <span className="text-gray-700">
                                    {member.forename} {member.surname}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {false && (
            <>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                    <Label>AI Quiz</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      onClick={generateQuiz}
                      disabled={!mainFile || generating}
                    >
                      {generating ? (
                        "Generating..."
                      ) : (
                        <span className="inline-flex items-center gap-2">
                          <Sparkles className="w-4 h-4" /> Generate Quiz
                        </span>
                      )}
                    </Button>
                    <Button variant="outline" onClick={addQuestion}>
                      <Plus className="w-4 h-4" /> Add Question
                    </Button>
                  </div>
                </div>
                {quiz && (
                  <div className="p-3 space-y-4 bg-white border border-gray-200 rounded-md">
                    <div className="text-sm font-medium text-gray-900">
                      Edit quiz ({quiz.questions.length} questions)
                    </div>
                    <div className="pr-1 space-y-4 overflow-y-auto max-h-80">
                      {quiz.questions.map((q, qi) => (
                        <div
                          key={q.id}
                          className="border border-gray-200 rounded-md"
                        >
                          <div className="flex items-center justify-between px-3 py-2 bg-gray-50">
                            <div className="text-sm font-medium text-gray-800">
                              Question {qi + 1}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => moveQuestion(qi, -1)}
                                disabled={qi === 0}
                              >
                                <ArrowUp className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => moveQuestion(qi, 1)}
                                disabled={qi === quiz.questions.length - 1}
                              >
                                <ArrowDown className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteQuestion(qi)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="p-3 space-y-3">
                            <div>
                              <Label>Question Text</Label>
                              <Input
                                value={q.question}
                                onChange={(e) =>
                                  updateQuestionText(qi, e.target.value)
                                }
                                className="mt-1"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Answers</Label>
                              <div className="space-y-2">
                                {q.options.map((opt, oi) => (
                                  <label
                                    key={oi}
                                    className="flex items-center gap-3"
                                  >
                                    <input
                                      type="radio"
                                      name={`correct-${q.id}`}
                                      checked={q.correctIndex === oi}
                                      onChange={() => setCorrectOption(qi, oi)}
                                      className="w-4 h-4"
                                    />
                                    <Input
                                      value={opt}
                                      onChange={(e) =>
                                        updateOptionText(qi, oi, e.target.value)
                                      }
                                      className="flex-1"
                                    />
                                    <span className="text-xs text-gray-500 whitespace-nowrap">
                                      {q.correctIndex === oi ? "Correct" : ""}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="text-xs text-gray-600">
                      This quiz will be assigned to{" "}
                      {
                        initialMembers.filter((m) =>
                          selectedRoles.includes(m.role),
                        ).length
                      }{" "}
                      member(s) across selected roles.
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
              disabled={isAddDocumentLoading}
            >
              Cancel
            </Button>
            {isEdit ? (
              <Button
                onClick={handleUpdate}
                disabled={!canProceed || isAddDocumentLoading}
              >
                {isAddDocumentLoading ? (
                  <span className="">Saving...</span>
                ) : (
                  "Save Changes"
                )}
              </Button>
            ) : (
              <Button onClick={handleCreate} disabled={!canProceed || isBulkUploading}>
                {isBulkUploading
                  ? `Uploading ${uploadFiles.filter((u) => u.status === "success").length}/${uploadFiles.length}...`
                  : uploadFiles.length > 1
                    ? `Upload ${uploadFiles.length} Documents`
                    : "Upload Document"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default NewDocumentModal;
